import React, { useState, useEffect } from 'react';
import { ChatMessage } from '../../services';

interface TypingMessageProps {
  message: ChatMessage;
  onComplete?: () => void;
  typingSpeed?: number;
}

export const TypingMessage: React.FC<TypingMessageProps> = ({
  message,
  onComplete,
  typingSpeed = 45 // Slightly faster for better UX
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  console.log('TypingMessage rendered with message:', message.content.substring(0, 50) + '...');

  // Click to skip typing animation
  const handleClick = () => {
    if (isTyping) {
      setDisplayText(message.content);
      setIsTyping(false);
      setIsComplete(true);
      onComplete?.();
    }
  };

  useEffect(() => {
    if (!message.content) {
      setDisplayText('');
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    setDisplayText('');
    setIsComplete(false);

    console.log('Starting word-by-word typing effect');

    // Split by spaces to get words, keep original spacing
    const words = message.content.split(' ');
    let currentWordIndex = 0;
    const startDelay = 100; // Reduced from 300ms
    // Use typingSpeed parameter: lower = faster (30-60 range recommended)
    const baseWordDelay = typingSpeed || 45;

    const typeNextWord = () => {
      if (currentWordIndex < words.length) {
        const word = words[currentWordIndex];

        // Add word with proper spacing
        setDisplayText(prev => {
          if (currentWordIndex === 0) {
            return word; // First word, no space
          } else {
            return prev + ' ' + word; // Subsequent words with space
          }
        });

        currentWordIndex++;

        // Calculate delay based on the word content
        let delay = baseWordDelay;

        // Extra pause for sentences (much shorter now)
        if (word.match(/[.!?]$/)) {
          delay = baseWordDelay * 2; // Shorter pause after sentences
        } else if (word.match(/[,;:]$/)) {
          delay = baseWordDelay * 1.2; // Shorter pause after clauses
        }

        // Add some randomness for natural feel
        delay = delay + (Math.random() * 20 - 10);

        setTimeout(typeNextWord, delay);
      } else {
        console.log('Word-by-word typing effect completed');
        setIsTyping(false);
        setIsComplete(true);
        onComplete?.();
      }
    };

    // Start typing after delay
    const timeoutId = setTimeout(typeNextWord, startDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [message.content, typingSpeed, onComplete]);

  console.log('TypingMessage state:', { isTyping, displayText: displayText.substring(0, 50) + '...' });

  return (
    <div
      className={`flex w-full ${isTyping ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
      title={isTyping ? 'Click to show full message' : ''}
    >
      <div className="flex-shrink-0 w-8 h-8 mr-4">
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-600 to-blue-600 text-white flex items-center justify-center">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {/* Message text with typing effect - ChatGPT style */}
        <div
          className={`text-base leading-relaxed text-gray-800 dark:text-gray-100 transition-opacity duration-200 ${
            isTyping ? 'opacity-90' : 'opacity-100'
          }`}
        >
          <div className="whitespace-pre-wrap">
            {displayText}
            {/* Typing cursor indicator - ChatGPT style */}
            {isTyping && (
              <span
                className="inline-block w-0.5 h-5 bg-gray-600 ml-1 animate-pulse"
                style={{
                  animation: 'blink 1s infinite'
                }}
              />
            )}
          </div>
        </div>

        {/* Typing indicator with skip hint - ChatGPT style */}
        {isTyping && (
          <div className="flex items-center space-x-2 mt-3">
            <div className="flex space-x-1">
              <div
                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: '160ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: '320ms' }}
              ></div>
            </div>
            <span className="text-xs text-gray-400 ml-2">Click to skip</span>
          </div>
        )}
      </div>
    </div>
  );
};
